import { Component, OnInit, Input, OnDestroy, ViewChild, EventEmitter, Output } from '@angular/core';
import { TitleCasePipe } from "@angular/common";
import { forkJoin, Subscription } from "rxjs";
import { tap, finalize } from "rxjs/operators";

import { GridOptions, ColDef, GridApi } from "ag-grid-community";

import { GridFormatterService } from '../../../common/services/grid-formatter.service';
import { CheckboxRendererComponent } from "../../../common/components/ag-grid/checkbox-renderer.component";
import { Well, Formation, EditedFormationRecord } from "../../../common/models/well.model";
import { DomainDefinitionService } from "../../../builder/services/domain-definition.service";
import { FormationModel } from "../../../form-controls/components/new-casing-plan/models/casing-plan.model";
import { IconActionRendererComponent } from "../../../common/components/ag-grid/icon-action-renderer.component";
import { EditFormationRecordComponent } from '../edit-formation-record/edit-formation-record.component';

@Component({
    selector: 'well-detail-formations',
    templateUrl: './well-detail-formations.component.html',
    styleUrls: ['./well-detail-formations.component.scss'],
    standalone: false
})
export class WellDetailFormationsComponent implements OnInit, OnDestroy {

	allFormations: FormationModel[] = [];
	allMethods: any[] = [];
	@Input() canEdit: boolean = false;

	currentFormations: Formation[] = [];

	dataReady: boolean = false;

	@ViewChild(EditFormationRecordComponent) public editor: EditFormationRecordComponent;

	@Output() formationRecordEdited: EventEmitter<EditedFormationRecord> = new EventEmitter<EditedFormationRecord>();

	gridOptions: GridOptions;
	gridApi?: GridApi;

	subscription: Subscription = new Subscription();// = Subscription.EMPTY;

	private _well: Well;

	get well(): Well {
		return this._well;
	}

	@Input() set well(value: Well) {
		this._well = value;
		if (value.formations != null) {
			value.formations.forEach(f => {
				if (f.top != null || f.base != null) {
					this.currentFormations.push(f);
				}
			})
		}
	}

	constructor(private _domain: DomainDefinitionService, private formatterService: GridFormatterService, private titleCasePipe: TitleCasePipe) { }


	ngOnInit() {
		const formation$ = this._domain.getValues("FORMATION").pipe(
			tap((formations: any[]) => {
				if (formations != null) {
					formations.forEach(f => {
						const model = new FormationModel();
						model.code = f.Code;
						model.definition = f.Definition;
						model.isActive = f.isActive;
						model.isRequired = f.isRequired;
						model.lastModifiedDateUTC = f.LastModifiedDateUTC;
						model.aapgCode = f.aapgCode;
						this.allFormations.push(model);
					});
				}
			})
		);
		const method$ = this._domain.getValues("METH_OBTND").pipe(
			tap((methods: any[]) => {
				if (methods != null) {
					this.allMethods = methods;
				}
			})
		);


		let form$ = forkJoin([formation$, method$]).pipe(
			finalize(() => {
				form$.unsubscribe();
			}))
			.subscribe(
				() => {
					this.dataReady = true;
				},
				(err) => {
					console.error(err);
				}
			);
		this.subscription.add(form$);
		this.gridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.buildColumns());
		this.gridOptions.headerHeight = 50;
		this.gridOptions.onGridReady = this.formatterService.gridReadySizeToFit;
		this.gridOptions.getRowClass = (params: any) => {
			if (params.data.comments != null && params.data.comments.length > 0) return 'red';
		};
	}

	ngOnDestroy() {
		if (this.subscription != null) {
			this.subscription.unsubscribe();
		}
	}

	buildColumns(): ColDef[] {
		let columns: ColDef[] = [
			{
				headerClass: "wrapped",
				headerName: "Formation",
				headerTooltip: "Formation",
				field: "selectedFormation",
				valueFormatter: this.formatFormation(),
				tooltipValueGetter: this.formatFormation(),
				filter: false,
				sortable: false,
				minWidth: 150,
				maxWidth: 200,
				pinned: "left"
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Top",
				headerTooltip: "Top",
				field: "top",
				tooltipField: "top",
				filter: false,
				sortable: false,
				minWidth: 125,
				maxWidth: 125
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Bottom",
				headerTooltip: "Bottom",
				field: "base",
				tooltipField: "base",
				filter: false,
				sortable: false,
				minWidth: 125,
				maxWidth: 125
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Producing Formation",
				headerTooltip: "Producing Formation",
				field: "producingFormation",
				cellRenderer: CheckboxRendererComponent,
					cellRendererParams: {
					action: () => { return false; },
					field: "producingFormation",
					description: "Producing Formation",
					isVisible: () => { return true; },
					isEnabled: () => { return false; },
					renderAsYesNo: true
				},
				tooltipValueGetter: this.formatterService.formatBooleanToYesNo("producingFormation"),
				filter: false,
				sortable: false,
				minWidth: 125,
				maxWidth: 125
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Method Obtained",
				headerTooltip: "Method Obtained",
				field: "methodObtained",
				valueFormatter: this.formatMethod(),
				tooltipValueGetter: this.formatMethod(),
				filter: false,
				sortable: false,
				minWidth: 125,
				maxWidth: 125
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Shows Of: Oil/Gas/Brine or Fresh Water",
				headerTooltip: "Shows Of: Oil/Gas/Brine or Fresh Water",
				field: "showsOf",
				tooltipField: "showsOf",
				filter: false,
				sortable: false,
				minWidth: 125
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Remarks",
				headerTooltip: "Remarks",
				field: "remarks",
				tooltipField: "remarks",
				filter: false,
				sortable: false,
				minWidth: 125
			} as ColDef
		];
		if (this.canEdit) {
			let editColumn: ColDef =
				{
					cellStyle: { 'text-align': 'right', 'line-height': 1.75 },
					cellRenderer: IconActionRendererComponent,
					cellRendererParams: {
						buttons: [
							{
								action: (obj) => this.editRecord(obj),
								label: 'Edit',
								icon: 'edit'
							}
						]
					},
					filter: false,
					sortable: false,
					minWidth: 50,
					maxWidth: 50,
					pinned: 'left'
				} as ColDef;
			columns.unshift(editColumn);
		}
		return columns;
	}

	editRecord(record: EditedFormationRecord): void {
		if (this.editor == null) {
			console.error('cannot find instance of formation editor');
			return;
		}
		this.editor.openForm(record);
	}

	formatFormation() {
		return (obj) => {
			let value = obj.value;
			if (value != null && this.allFormations != null) {
				let formation = this.allFormations.find(f => f.code == value);
				if (formation != null) {
					value = formation.definition;
				}
			}
			if (value != null) {
				return this.titleCasePipe.transform(value);
			}
			return value;
		}
	}

	formatMethod() {
		return (obj) => {
			let value = obj.value;
			if (value != null && this.allMethods != null) {
				let method = this.allMethods.find(f => f.Code == value);
				if (method != null) {
					value = method.Definition;
				}
			}
			return value;
		}
	}


	onGridReady(event: any) {
		this.formatterService.gridReadySizeToFit(event);
		if (event.api) {
			this.gridApi = event.api;
		}
	}

	recordEdited(edit: EditedFormationRecord): void {
		console.log('record in formations component', edit);
		let index = this.currentFormations.findIndex(c => c.id === edit.id);
		if (index != -1) {
			console.log('before', this.currentFormations);
			this.currentFormations[index] = edit;
			console.log('after', this.currentFormations);
			this.gridApi.setGridOption("rowData", []);
			this.gridApi.setGridOption("loading", true);
			setTimeout(() => {
				this.gridApi.setGridOption("rowData", this.currentFormations);
				this.gridApi.setGridOption("loading", false);
			}, 500);
		}
		console.log('emitting from well detail formations', edit);
		this.formationRecordEdited.emit(edit);
	}

	sizeColumnsToFit(): void {
		if (this.gridApi != null) {
			this.gridApi.sizeColumnsToFit();
		}
	}


}
